import { useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Anchor, Button, Container, Group, Skeleton, Stack } from '@mantine/core';
import { useMutation } from '@tanstack/react-query';
import { notifications } from '@mantine/notifications';
import { IconArrowLeft } from '@tabler/icons-react';
import LotStatus, { type ExchangeStatusesState } from '@/components/app/sell/LotStatus';
import ExchangeOrientationCard from '@/components/app/sell/ExchangeOrientationCard';
import SaleOfferTerms from '@/components/app/sell/SaleOfferTerms';
import FormMessage from '@/components/auth/FormMessage';
import BestPriceLink from '@/components/common/BestPriceLink';
import FactsStrip, { type Fact } from '@/components/common/FactsStrip';
import PageTitle from '@/components/common/PageTitle';
import { toPercent } from '@/components/common/PrepaymentBadge';
import { orderService } from '@/api/services/OrderService';
import { getApiMessage, getStatus, isServiceUnavailable } from '@/api/errors';
import { useExchangeOrientation, useExchangeSession, useExchangeStatuses, useSaleOffer, useSellOfferActions } from '@/hooks/useSell';
import { useSaleBestOffersMap } from '@/hooks/useBestOffers';
import { bestOfferLink, bestPriceTone } from '@/lib/bestOffer';
import { exchangeUiState, lotActions } from '@/lib/exchangeOffer';
import { loadingWindow, mainQuality } from '@/lib/sellOffer';
import { makeSellEditUrl } from '@/lib/sellLinks';
import { formatMoney, formatOrgName } from '@/lib/format';
import { ROUTES } from '@/routes/paths';
import type { SaleOrder } from '@/types/sell';
import classes from './SellOfferView.module.css';

/** Пояснение для лота, с которым продавец уже ничего не делает: ключ текста и нужна ли ссылка в «Мои сделки». */
function closedNote(offer: SaleOrder, matched: boolean): { readonly key: string; readonly toDeals: boolean } | null {
  if (matched || offer.status === 'UNDER_BARGAIN') {
    return { key: 'app.sell.offer.inDeal', toDeals: true };
  }
  switch (offer.status) {
    case 'ACTIVE':
    case 'SUSPENDED':
      return null;
    case 'CLOSED':
      return { key: 'app.sell.offer.closed', toDeals: true };
    case 'MODERATED':
      return { key: 'app.sell.offer.moderated', toDeals: false };
    case 'DRAFT':
      return { key: 'app.sell.offer.draft', toDeals: false };
    default:
      return { key: 'app.sell.offer.canceled', toDeals: false };
  }
}

function BackLink() {
  const { t } = useTranslation();
  return (
    <Anchor component={Link} to={ROUTES.APP_SELL} c="gray.7" fz="sm" underline="never" style={{ alignSelf: 'flex-start' }}>
      <Group gap={4} wrap="nowrap">
        <IconArrowLeft size={16} aria-hidden />
        {t('app.sell.offerBack')}
      </Group>
    </Anchor>
  );
}

// Страница пересоздаётся при смене номера в адресе: незавершённое действие прежнего предложения (например, переход
// в список после удаления) не должно сработать на другом предложении.
export default function SellOfferView() {
  const [params] = useSearchParams();
  const orderIdRaw = params.get('orderId') ?? '';
  return <SellOfferPage key={orderIdRaw} orderIdRaw={orderIdRaw} />;
}

// Страница своего предложения о продаже («Бюллетень», образец b-deal): шапка с ценой, полоса фактов с одним итоговым
// статусом, действия по общему правилу `lotActions` (как в таблице «Продать»), ориентир цены и условия предложения.
// Загружается по одному номеру `orderId`; неверный номер, удалённое и чужое предложение — «не найдено».
function SellOfferPage({ orderIdRaw }: { readonly orderIdRaw: string }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const orderId = /^[1-9]\d*$/.test(orderIdRaw) ? Number(orderIdRaw) : null;

  const offerQuery = useSaleOffer(orderId);
  const offer = offerQuery.data;
  const statuses = useExchangeStatuses(offer?.toExchange && orderId != null ? [orderId] : []);
  const session = useExchangeSession();
  const orientation = useExchangeOrientation(orderId, Boolean(offer?.toExchange));
  const bestOffer = useSaleBestOffersMap().get(orderId ?? -1);
  const actions = useSellOfferActions();
  const download = useMutation({
    mutationFn: (fileId: number) => orderService.downloadOrderFile(fileId),
    onError: (err) =>
      notifications.show({
        color: 'red',
        message: isServiceUnavailable(err) ? t('auth.errors.unavailable') : (getApiMessage(err) ?? t('auth.errors.generic')),
      }),
  });
  // Во время действия нажатая кнопка неактивна, а после него может исчезнуть (пауза сменяется возобновлением), и фокус
  // уходит в начало документа. Поэтому после завершения, успешного или нет, фокус переводим на первую доступную
  // кнопку ряда действий, а если её нет — на сам ряд.
  const actionsRef = useRef<HTMLDivElement>(null);
  const refocus = (): void => {
    requestAnimationFrame(() => {
      const group = actionsRef.current;
      (group?.querySelector<HTMLElement>('button:not(:disabled), a[href]') ?? group)?.focus();
    });
  };
  const settle = { onSettled: refocus };

  const status = getStatus(offerQuery.error);
  if (orderId == null || (offerQuery.isError && (status === 404 || status === 403))) {
    return (
      <Container size="lg" py="xl">
        <Stack gap="lg">
          <BackLink />
          <PageTitle c="var(--mantine-color-text)" fz={{ base: 26, sm: 30 }}>
            {t('app.sell.offer.title')}
          </PageTitle>
          <FormMessage>{t('app.sell.offer.notFound')}</FormMessage>
        </Stack>
      </Container>
    );
  }

  // Сбой повторной загрузки (например, после действия) не прячет уже показанное предложение: экран ошибки — только
  // когда показывать нечего.
  if (offerQuery.isError && !offer) {
    return (
      <Container size="lg" py="xl">
        <Stack gap="lg">
          <BackLink />
          <PageTitle c="var(--mantine-color-text)" fz={{ base: 26, sm: 30 }}>
            {t('app.sell.offer.title')}
          </PageTitle>
          <FormMessage>
            <Group gap="sm">
              <span>{isServiceUnavailable(offerQuery.error) ? t('auth.errors.unavailable') : t('auth.errors.generic')}</span>
              <Button variant="default" size="compact-md" loading={offerQuery.isFetching} onClick={() => offerQuery.refetch()}>
                {t('common.retry')}
              </Button>
            </Group>
          </FormMessage>
        </Stack>
      </Container>
    );
  }

  if (!offer) {
    // Скелет повторяет будущую разметку: шапку, полосу фактов, действия и раздел условий.
    return (
      <Container size="lg" py="xl">
        <Stack gap="lg" aria-busy="true" aria-label={t('app.sell.offer.loading')}>
          <BackLink />
          <Stack gap="sm">
            <Skeleton h={36} w={320} />
            <Skeleton h={20} w={260} />
          </Stack>
          <Skeleton h={64} />
          <Skeleton h={42} w={360} />
          <Skeleton h={240} />
        </Stack>
      </Container>
    );
  }

  const exchangeState: ExchangeStatusesState = !offer.toExchange || statuses.data ? 'ready' : statuses.isError ? 'failed' : 'loading';
  const resolved = exchangeState === 'ready';
  const exState = resolved ? exchangeUiState(offer, statuses.data?.[offer.id]) : null;
  const can = lotActions(offer, exState, resolved);
  const note = closedNote(offer, exState === 'MATCHED');
  const failReason = exState === 'FAILED' ? statuses.data?.[offer.id]?.reason : null;
  const busy = actions.anyPending;

  const quality = mainQuality(offer);
  const lead = [
    offer.cropYear ? t('app.sell.offer.cropYear', { year: offer.cropYear }) : null,
    quality,
    offer.updatedDate ? t('app.sell.offer.updated', { date: offer.updatedDate }) : null,
  ]
    .filter(Boolean)
    .join(' · ');
  const percent = toPercent(offer.prepaymentInfo?.percent ?? offer.prepaymentInfo?.type ?? offer.prepaymentInfo?.name);
  const window = loadingWindow(offer, (date) => t('app.sell.windowFrom', { date }));
  const total = offer.price != null && offer.weightTon != null ? offer.price * offer.weightTon : null;

  const factList: (Fact | null)[] = [
    offer.addressTitle || offer.address
      ? {
          key: 'basis',
          label: t('app.sell.offer.facts.basis'),
          value: formatOrgName(offer.addressTitle ?? offer.address),
          note: offer.addressTitle ? offer.address : undefined,
          wide: true,
        }
      : null,
    offer.weightTon != null
      ? { key: 'weight', label: t('app.sell.offer.facts.weight'), value: `${formatMoney(offer.weightTon)}\u00a0${t('app.sell.tons')}` }
      : null,
    window ? { key: 'window', label: t('app.sell.offer.facts.window'), value: window } : null,
    percent != null
      ? {
          key: 'prepayment',
          label: t('app.sell.offer.facts.prepayment'),
          value: percent === 0 ? t('app.sell.prepayment0Short') : `${percent}\u00a0%`,
        }
      : null,
    {
      key: 'status',
      label: t('app.sell.offer.facts.status'),
      value: <LotStatus offer={offer} state={exState} exchangeState={exchangeState} />,
    },
  ];
  const facts = factList.filter((fact): fact is Fact => fact != null);

  const publishLabel = t(exState === 'FAILED' ? 'app.sell.exchange.republish' : 'app.sell.exchange.publish');
  const hasButtons = Object.values(can).some(Boolean);

  return (
    <Container size="lg" py="xl">
      <Stack gap="xl">
        <Stack gap="sm">
          <BackLink />
          <div>
            <div className={classes.head}>
              <div style={{ minWidth: 0 }}>
                <PageTitle c="var(--mantine-color-text)" fz={{ base: 26, sm: 30 }}>
                  {offer.goodsType ?? quality ?? '—'}
                </PageTitle>
                {lead ? <p className={classes.lead}>{lead}</p> : null}
              </div>
              {offer.price != null ? (
                <div className={classes.price}>
                  <p className={classes.priceLabel}>{t('app.sell.offer.priceLabel')}</p>
                  <p className={classes.priceValue}>
                    {formatMoney(offer.price)} <span className={classes.priceUnit}>{t('app.deals.rubPerTon')}</span>
                  </p>
                  {total != null && offer.weightTon != null ? (
                    <p className={classes.priceTotal}>
                      {t('app.sell.offer.total', { weight: formatMoney(offer.weightTon), amount: formatMoney(total) })}
                    </p>
                  ) : null}
                </div>
              ) : null}
            </div>
            <FactsStrip facts={facts} />
          </div>
        </Stack>

        <Stack gap="md">
          {note ? (
            <FormMessage tone="info">
              <Group gap="sm">
                <span>{t(note.key)}</span>
                {note.toDeals ? (
                  <Anchor component={Link} to={ROUTES.APP_DEALS} underline="always">
                    {t('app.sell.offer.toDeals')}
                  </Anchor>
                ) : null}
              </Group>
            </FormMessage>
          ) : null}

          {exchangeState === 'failed' && !note ? (
            <FormMessage>
              <Group gap="sm">
                <span>{t('app.sell.exchange.statusError')}</span>
                <Button variant="default" size="compact-md" loading={statuses.isFetching} onClick={() => statuses.refetch()}>
                  {t('common.retry')}
                </Button>
              </Group>
            </FormMessage>
          ) : null}

          {/* Причина отказа — справка о состоянии лота, а не ответ на действие: без срочного объявления. */}
          {failReason ? <FormMessage quiet>{t('app.sell.exchange.failedReason', { reason: failReason })}</FormMessage> : null}
          {can.removeFromExchange ? <FormMessage tone="info">{t('app.sell.exchange.frozenHint')}</FormMessage> : null}

          {hasButtons ? (
            <div ref={actionsRef} tabIndex={-1} role="group" aria-label={t('app.sell.offer.actions')} className={classes.actions}>
              {can.publish && session.data?.open ? (
                <Button
                  loading={actions.publishExchange.isPending}
                  disabled={busy}
                  onClick={() => actions.publishExchange.mutate(offer.id, settle)}
                >
                  {publishLabel}
                </Button>
              ) : null}
              {can.resume ? (
                <Button
                  loading={actions.unsuspend.isPending}
                  disabled={busy}
                  onClick={() => actions.unsuspend.mutate(offer.id, settle)}
                >
                  {t('app.sell.lot.resume')}
                </Button>
              ) : null}
              {can.removeFromExchange ? (
                <Button
                  variant="default"
                  loading={actions.removeExchange.isPending}
                  disabled={busy}
                  onClick={() => actions.confirmRemoveFromExchange(offer.id, refocus)}
                >
                  {t('app.sell.exchange.removeAction')}
                </Button>
              ) : null}
              {can.edit ? (
                // Пока выполняется действие, правка недоступна: ссылка не может быть неактивной, поэтому — кнопка.
                busy ? (
                  <Button variant="default" disabled>
                    {t('app.sell.lot.edit')}
                  </Button>
                ) : (
                  <Button component={Link} to={makeSellEditUrl(offer.id)} variant="default">
                    {t('app.sell.lot.edit')}
                  </Button>
                )
              ) : null}
              {can.suspend ? (
                <Button
                  variant="default"
                  loading={actions.suspend.isPending}
                  disabled={busy}
                  onClick={() => actions.suspend.mutate(offer.id, settle)}
                >
                  {t('app.sell.lot.suspend')}
                </Button>
              ) : null}
              {can.remove ? (
                <Button
                  variant="subtle"
                  color="red"
                  className={classes.remove}
                  loading={actions.removeOffer.isPending}
                  disabled={busy}
                  onClick={() => actions.confirmDeleteOffer(offer.id, () => navigate(ROUTES.APP_SELL))}
                >
                  {t('app.sell.lot.delete')}
                </Button>
              ) : null}
            </div>
          ) : null}

          {can.publish && session.data && !session.data.open ? (
            <p className={classes.hint}>
              {t('app.sell.offer.windowClosed', { start: session.data.windowStart, end: session.data.windowEnd })}
            </p>
          ) : null}
          {can.publish && session.isError && !session.data ? (
            <FormMessage>
              <Group gap="sm">
                <span>{t('app.sell.offer.sessionError')}</span>
                <Button variant="default" size="compact-md" loading={session.isFetching} onClick={() => session.refetch()}>
                  {t('common.retry')}
                </Button>
              </Group>
            </FormMessage>
          ) : null}
        </Stack>

        {!note && !offer.toExchange && bestOffer?.bestPrice != null ? (
          <div className={classes.best}>
            <p className={classes.bestTitle}>{t('app.sell.offerBestPriceHint')}</p>
            <BestPriceLink
              to={bestOfferLink(bestOffer)}
              price={bestOffer.bestPrice}
              action="sell"
              tone={bestPriceTone(bestOffer.advantage)}
              advantage={bestOffer.advantage}
            />
          </div>
        ) : null}
        {!note && offer.toExchange && orientation.data ? <ExchangeOrientationCard orientation={orientation.data} /> : null}

        <SaleOfferTerms
          offer={offer}
          onDownload={(id) => download.mutate(id)}
          downloadingId={download.isPending ? download.variables : undefined}
        />
      </Stack>
    </Container>
  );
}
